import { X, FileText, Download, Calendar, DollarSign, Building, CheckCircle, AlertCircle, Image, ArrowUp, ArrowDown, Clock, File as FileIcon, ExternalLink } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const LancamentoDetailModal = ({ lancamento, onClose, onReload, onOpenPayment }) => {
  // --- TOTAIS (preferir view: total_pago/saldo, com fallback no array de pagamentos)
  const total = Number(lancamento.valor || 0);
  const pagoFromArray = lancamento.pagamentos?.reduce((sum, p) => sum + Number(p.valor_pago || p.valor), 0) || 0;
  const valorPago = Number(lancamento.total_pago ?? pagoFromArray);
  const valorRestante = Number(lancamento.saldo ?? Math.max(0, total - valorPago));

  // --- Helpers
  const safeParseISO = (d) => {
    try {
      const parsed = parseISO(d);
      return isValid(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  // --- Status e Prazo (derivado dos totais acima)
  const calcularStatusEPrazo = (lanc) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const vencimento = safeParseISO(lanc.data_vencimento);
    const diasDiferenca = vencimento ? Math.floor((new Date(vencimento.setHours(0,0,0,0)) - hoje) / (1000 * 60 * 60 * 24)) : null;

    const badges = [];

    // Pagamento
    if (valorRestante <= 0 && total > 0) {
      badges.push({ tipo: 'pagamento', label: 'Concluída', color: 'green', icon: CheckCircle });
    } else if (valorPago > 0 && valorRestante > 0) {
      badges.push({ tipo: 'pagamento', label: 'Parcial', color: 'yellow', icon: AlertCircle });
    }

    // Prazo (só se não estiver totalmente pago)
    if (!(valorRestante <= 0 && total > 0) && diasDiferenca !== null) {
      if (diasDiferenca < 0) {
        badges.push({
          tipo: 'prazo',
          label: 'Vencida',
          color: 'red',
          icon: AlertCircle,
          prazo: Math.abs(diasDiferenca),
          prazoLabel: `${Math.abs(diasDiferenca)} ${Math.abs(diasDiferenca) === 1 ? 'dia' : 'dias'}`
        });
      } else if (diasDiferenca === 0) {
        badges.push({
          tipo: 'prazo',
          label: 'Vence Hoje',
          color: 'orange',
          icon: Clock,
          prazo: 0,
          prazoLabel: 'Hoje'
        });
      } else {
        badges.push({
          tipo: 'prazo',
          label: 'Pendente',
          color: 'blue',
          icon: Clock,
          prazo: diasDiferenca,
          prazoLabel: `${diasDiferenca} ${diasDiferenca === 1 ? 'dia' : 'dias'}`
        });
      }
    }

    return badges;
  };

   // --------- NOVO: helpers de anexo (baseado no retorno do backend por pagamento)
  const pagamentos = Array.isArray(lancamento.pagamentos)
    ? lancamento.pagamentos.map((p) => {
        const resolvedUrl = p?.comprovante_url && String(p.comprovante_url).trim().length > 0
          ? p.comprovante_url
          : null;

        return {
          ...p,
          comprovante_url: resolvedUrl,
          comprovante_mime: p?.comprovante_mime || '',
          comprovante_nome: p?.comprovante_nome || null
        };
      })
    : [];

  const anexos = pagamentos
    .map(p => ({
      id: p.id,
      url: p.comprovante_url,
      mime: p.comprovante_mime || '',
      nome: p.comprovante_nome || (p.id ? `comprovante-${p.id}` : 'comprovante')
    }))
    .filter(a => !!a.url);

  const isImage = (mime = '', url = '') => {
    if (mime && /^image\//i.test(mime)) return true;
    return /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(url);
  };

  const isPdf = (mime = '', url = '') => {
    if (mime && /^application\/pdf$/i.test(mime)) return true;
    return /\.pdf(\?|$)/i.test(url);
  };

  const fileLabel  = (mime = '', url = '') => {
    if (isImage(mime, url)) return 'Imagem';
    if (isPdf(mime, url)) return 'PDF';
    if (mime) return mime;
    return 'Arquivo';
  };


  const badges = calcularStatusEPrazo(lancamento);

  const statusColors = {
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
  };

  const tipoColors = {
    pagar: 'bg-red-500/20 text-red-400 border-red-500/30',
    receber: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  const formatarDescricao = (lanc) => {
    if (lanc.nota_fiscal_id) {
      const nfRef = `NF ${lanc.nota_numero || lanc.nota_fiscal_id}`;
      const parcelaRef = lanc.parcela_numero ? ` - Parcela ${String(lanc.parcela_numero).padStart(3, '0')}` : '';
      return `${nfRef}${parcelaRef}`;
    }
    return lanc.descricao;
  };

  const handleOpenReport = () => {
    window.open(`/api/lancamentos/${lancamento.id}/relatorio`, '_blank', 'noopener,noreferrer');
  };

    // Linha do tempo
  const criarLinhaDoTempo = () => {
    const eventos = [];
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const venc = safeParseISO(lancamento.data_vencimento);
    const diasDiferenca = venc ? Math.floor((new Date(venc.setHours(0,0,0,0)) - hoje) / (1000 * 60 * 60 * 24)) : null;

    // Vencimento
    if (venc) {
      eventos.push({
        tipo: 'vencimento',
        data: lancamento.data_vencimento,
        titulo: diasDiferenca < 0 ? 'Vencido' : diasDiferenca === 0 ? 'Vence Hoje' : 'Vencimento',
        descricao: diasDiferenca < 0
          ? `Venceu há ${Math.abs(diasDiferenca)} ${Math.abs(diasDiferenca) === 1 ? 'dia' : 'dias'}`
          : diasDiferenca === 0
            ? 'Vence hoje'
            : `Vence em ${diasDiferenca} ${diasDiferenca === 1 ? 'dia' : 'dias'}`,
        icon: Calendar,
        cor: diasDiferenca < 0 ? 'red' : diasDiferenca === 0 ? 'orange' : 'blue'
      });
    }

    // Pagamentos
    if (lancamento.pagamentos && lancamento.pagamentos.length > 0) {
      lancamento.pagamentos.forEach((pag, i) => {
        const temComprovante = !!pag.comprovante_url;
        eventos.push({
          tipo: 'pagamento',
          data: pag.data_pagamento,
          titulo: `Pagamento ${i + 1}`,
          descricao:
            `${fmtBRL(Number(pag.valor_pago || pag.valor))}` +
            (pag.observacoes ? ` - ${pag.observacoes}` : '') +
            (temComprovante ? ` — possui comprovante` : ''),
          comprovante_url: temComprovante ? pag.comprovante_url : null,
          comprovante_nome: temComprovante ? (pag.comprovante_nome || null) : null,
          icon: DollarSign,
          cor: 'green'
        });
      });
    }

    // Criação
    if (lancamento.created_at) {
      eventos.push({
        tipo: 'criacao',
        data: lancamento.created_at,
        titulo: 'Lançamento Criado',
        descricao: 'Lançamento registrado no sistema',
        icon: FileText,
        cor: 'gray'
      });
    }

    // Saldo atual
    eventos.push({
      tipo: 'saldo',
      data: new Date().toISOString(),
      titulo: 'Saldo Atual',
      descricao: `${fmtBRL(total)} − ${fmtBRL(valorPago)} = ${fmtBRL(valorRestante)}`,
      icon: DollarSign,
      cor: 'orange'
    });

    // Mais recente primeiro
    return eventos.sort((a, b) => new Date(b.data) - new Date(a.data));
  };

  const linhaDoTempo = criarLinhaDoTempo();

  const coresIcones = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    orange: 'bg-orange-500/20 text-orange-400',
    red: 'bg-red-500/20 text-red-400',
    gray: 'bg-gray-500/20 text-gray-400'
  };

  const empresa = lancamento.empresa
    || (lancamento.tipo === 'pagar' ? lancamento.fornecedor : lancamento.cliente)
    || 'Não informado';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-6 space-y-4 z-30">
          <div className="flex justify-between items-start">
            <h2 className="text-2xl font-bold">{formatarDescricao(lancamento)}</h2>
            <button
              onClick={onClose}
              className="icon-button"
            >
              <X size={24} />
            </button>
          </div>

          {/* BADGES: TIPO + STATUS(ES) + PRAZO */}
          <div className="flex flex-wrap gap-2">
            {/* TIPO */}
            <span className={`px-3 py-1 rounded-full text-sm font-medium border inline-flex items-center gap-1.5 ${tipoColors[lancamento.tipo]}`}>
              {lancamento.tipo === 'pagar' ? (
                <>
                  <ArrowUp size={14} />
                  A Pagar
                </>
              ) : (
                <>
                  <ArrowDown size={14} />
                  A Receber
                </>
              )}
            </span>

            {/* STATUS */}
            {badges.map((badge, index) => {
              const BadgeIcon = badge.icon;
              return (
                <span
                  key={index}
                  className={`px-3 py-1 rounded-full text-sm font-medium border inline-flex items-center gap-1.5 ${statusColors[badge.color]}`}
                >
                  <BadgeIcon size={14} />
                  {badge.label}
                </span>
              );
            })}

            {/* PRAZO LABEL */}
            {badges.find(b => b.tipo === 'prazo' && b.prazo !== null) && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium border inline-flex items-center gap-1.5 ${
                badges.find(b => b.tipo === 'prazo').color === 'red' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                badges.find(b => b.tipo === 'prazo').color === 'orange' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                'bg-gray-500/10 text-gray-400 border-gray-500/20'
              }`}>
                <Clock size={14} />
                {badges.find(b => b.tipo === 'prazo').prazoLabel}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Valores */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Valor Total</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmtBRL(total)}</div>
            </div>

            <div className="rounded-xl border border-success-200 dark:border-success-700 bg-success-50 dark:bg-success-900/20 p-4">
              <div className="text-sm text-success-700 dark:text-success-400 mb-1">Valor Pago</div>
              <div className="text-2xl font-bold text-success-700 dark:text-success-300">{fmtBRL(valorPago)}</div>
            </div>

            <div className="rounded-xl border border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20 p-4">
              <div className="text-sm text-warning-700 dark:text-warning-400 mb-1">Valor Restante</div>
              <div className="text-2xl font-bold text-warning-700 dark:text-warning-300">{fmtBRL(valorRestante)}</div>
            </div>
          </div>

          {(valorPago > 0 && valorRestante > 0) && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 font-mono">
              {fmtBRL(total)} − {fmtBRL(valorPago)} = <span className="text-warning-600 dark:text-warning-400">{fmtBRL(valorRestante)}</span>
            </div>
          )}

          {/* Linha do Tempo */}
          <div>
            <h3 className="font-semibold text-lg border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">Linha do Tempo</h3>
            <div className="relative pl-4 sm:pl-6">
              <div className="absolute left-[2.25rem] sm:left-[2.75rem] top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-6">
                {linhaDoTempo.map((evento, index) => {
                  const EventoIcon = evento.icon;
                  const dataParsed = safeParseISO(evento.data);
                  return (
                    <div key={index} className="relative grid grid-cols-[auto,1fr] items-start gap-4">
                      <div className={`relative w-10 h-10 rounded-full ${coresIcones[evento.cor]} flex items-center justify-center flex-shrink-0`}>
                        <EventoIcon size={20} />
                      </div>
                      <div className="ml-2 sm:ml-3 flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
                        <div className="flex justify-between items-start mb-1 gap-4">
                          <div className="font-semibold text-gray-900 dark:text-gray-100">{evento.titulo}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {dataParsed ? format(dataParsed, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '—'}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{evento.descricao}</div>
                        {evento.tipo === 'pagamento' && evento.comprovante_url && (
                          <div className="mt-2">
                            <a
                              href={evento.comprovante_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 text-sm"
                            ><ExternalLink size={14}/> {evento.comprovante_nome ? `Abrir ${evento.comprovante_nome}` : 'Ver comprovante'}</a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Informações Gerais */}
          <div>
            <h3 className="font-semibold text-lg border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">Informações Gerais</h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Empresa */}
              {!!empresa && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
                  <div className="flex items-start gap-3">
                    <Building className="text-gray-500 dark:text-gray-400 mt-1 flex-shrink-0" size={18} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {lancamento.tipo === 'pagar' ? 'Fornecedor' : 'Cliente'}
                      </div>
                      <div className="text-gray-900 dark:text-gray-100 font-medium break-words">{empresa}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Nota Fiscal */}
              {lancamento.nota_fiscal_id && (
                <div className="rounded-xl border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 p-4">
                  <div className="text-primary-600 dark:text-primary-400 text-sm mb-2 flex items-center gap-2">
                    <FileText size={16} />
                    Vinculado à Nota Fiscal
                  </div>
                  <div className="text-gray-900 dark:text-gray-100 font-medium">
                    NF #{lancamento.nota_numero || lancamento.nota_fiscal_id}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Observações */}
          {lancamento.observacoes && (
            <div>
              <h3 className="font-semibold text-lg border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">Observações</h3>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{lancamento.observacoes}</p>
              </div>
            </div>
          )}

          {/* Comprovantes */}
          {anexos.length > 0 && (
            <div>
              <h3 className="font-semibold text-lg border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">Comprovantes Anexados</h3>
              <div className="grid grid-cols-2 gap-4">
              {anexos.map((a, index) => {
                  const nomeArquivo = a.nome || (a.url ? a.url.split('/').pop() : 'comprovante');
                  const ehImagem = isImage(a.mime, a.url);
                  const ehPdf = isPdf(a.mime, a.url);
                  return (
                    <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                        {ehImagem ? (
                            <Image className="text-primary-500 dark:text-primary-400 flex-shrink-0" size={20} />
                          ) : (
                            <FileIcon className="text-primary-500 dark:text-primary-400 flex-shrink-0" size={20} />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-gray-900 dark:text-gray-100 font-medium truncate">{nomeArquivo}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">{fileLabel(a.mime, a.url)}</div>
                          </div>
                        </div>
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={a.nome || undefined}
                          className="text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 transition-colors flex-shrink-0 ml-2"
                        >
                          <Download size={18} />
                        </a>
                      </div>
                      {ehImagem && (
                        <img
                        src={a.url}
                          alt="Comprovante"
                          className="w-full h-32 object-cover rounded border border-gray-200 dark:border-gray-700"
                          onError={(e) => (e.currentTarget.style.display = 'none')}
                        />
                      )}
                      {ehPdf && (
                        <div className="mt-2 text-sm">
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            <ExternalLink size={14} />
                            Abrir {a.nome || 'PDF do comprovante'}
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-6 flex justify-between gap-3 z-30">
          <button
            onClick={handleOpenReport}
            className="btn-secondary flex items-center gap-2"
          >
            <FileText size={18} />
            Abrir Relatório
          </button>

          <div className="flex gap-3">
            {valorRestante > 0 && onOpenPayment && (
              <button
                onClick={() => onOpenPayment(lancamento)}
                className="btn-success flex items-center gap-2"
              >
                <DollarSign size={18} />
                Registrar Pagamento
              </button>
            )}
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LancamentoDetailModal;
